/**
 * ChessPuzzle - Interactive chess puzzle component
 *
 * Displays a chess position and allows players to make moves.
 * Validates moves against solution and auto-plays opponent responses.
 * Shows feedback for wrong moves.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Chessboard } from 'react-chessboard';
import type { Square, Piece } from 'react-chessboard/dist/chessboard/types';
import { Chess } from 'chess.js';

interface ChessPuzzleConfig {
  fen: string;
  rating?: number;
  themes?: string[];
  puzzleId?: string;
}

interface ChessPuzzleProps {
  config: ChessPuzzleConfig;
  onSubmit: (moves: string) => void;
  disabled?: boolean;
  /** Solution moves in UCI notation (space-separated) */
  solution?: string;
  /** Whether to show solution playback mode */
  showSolution?: boolean;
}

// Parse UCI move string to from/to squares
function parseUCI(uci: string): { from: string; to: string; promotion?: string } {
  return {
    from: uci.slice(0, 2),
    to: uci.slice(2, 4),
    promotion: uci.length > 4 ? uci[4] : undefined,
  };
}

// Convert to UCI notation
function toUCI(from: string, to: string, promotion?: string): string {
  return from + to + (promotion || '');
}

export function ChessPuzzle({ config, onSubmit, disabled = false, solution, showSolution = false }: ChessPuzzleProps) {
  // Game state - use ref to avoid stale closures in callbacks
  const gameRef = useRef(new Chess(config.fen));
  const [gameKey, setGameKey] = useState(0); // Force re-render when game changes

  const [moveHistory, setMoveHistory] = useState<string[]>([]);
  const [highlightSquares, setHighlightSquares] = useState<Record<string, { background: string }>>({});

  // Puzzle progress
  const [puzzleMoveIndex, setPuzzleMoveIndex] = useState(0);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);
  const [puzzleComplete, setPuzzleComplete] = useState(false);
  const [wrongMove, setWrongMove] = useState(false);
  const hasSubmitted = useRef(false);

  // Solution playback state (for review mode)
  const [solutionIndex, setSolutionIndex] = useState(0);
  const [solutionGame, setSolutionGame] = useState<Chess | null>(null);
  const [isPlayingSolution, setIsPlayingSolution] = useState(false);

  // Parse solution moves
  const solutionMoves = useMemo(() => {
    if (!solution) return [];
    return solution.trim().split(/\s+/).filter(m => m);
  }, [solution]);

  // Board orientation based on whose turn in initial position
  const boardOrientation = useMemo((): 'white' | 'black' => {
    const initialGame = new Chess(config.fen);
    return initialGame.turn() === 'w' ? 'white' : 'black';
  }, [config.fen]);

  // Reset when config changes (new puzzle)
  useEffect(() => {
    gameRef.current = new Chess(config.fen);
    setGameKey(k => k + 1);
    setMoveHistory([]);
    setHighlightSquares({});
    setPuzzleMoveIndex(0);
    setWaitingForOpponent(false);
    setPuzzleComplete(false);
    setWrongMove(false);
    hasSubmitted.current = false;
  }, [config.fen]);

  // Initialize solution playback when showSolution becomes true
  useEffect(() => {
    if (showSolution && solution) {
      const newGame = new Chess(config.fen);
      setSolutionGame(newGame);
      setSolutionIndex(0);
      setHighlightSquares({});
    }
  }, [showSolution, solution, config.fen]);

  // Auto-play solution in review mode
  useEffect(() => {
    if (!isPlayingSolution || !solutionGame || solutionIndex >= solutionMoves.length) {
      setIsPlayingSolution(false);
      return;
    }

    const timer = setTimeout(() => {
      playSolutionMove(solutionIndex);
      setSolutionIndex(prev => prev + 1);
    }, 800);

    return () => clearTimeout(timer);
  }, [isPlayingSolution, solutionIndex, solutionMoves.length]);

  // Auto-submit when puzzle is complete
  useEffect(() => {
    if (puzzleComplete && moveHistory.length > 0 && !hasSubmitted.current) {
      hasSubmitted.current = true;
      const timer = setTimeout(() => {
        onSubmit(moveHistory.join(' '));
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [puzzleComplete, moveHistory, onSubmit]);

  // Play a single solution move (for review mode)
  const playSolutionMove = useCallback((index: number) => {
    if (!solutionGame || index >= solutionMoves.length) return;

    const uci = solutionMoves[index];
    const { from, to, promotion } = parseUCI(uci);

    try {
      solutionGame.move({ from, to, promotion });
      const newGame = new Chess(solutionGame.fen());
      setSolutionGame(newGame);

      const isPlayerMove = index % 2 === 0;
      const color = isPlayerMove
        ? 'rgba(0, 200, 0, 0.5)'
        : 'rgba(255, 165, 0, 0.5)';

      setHighlightSquares({
        [from]: { background: color },
        [to]: { background: color },
      });
    } catch (e) {
      console.error('Invalid solution move:', uci, e);
    }
  }, [solutionGame, solutionMoves]);

  // Step through solution manually (review mode)
  const stepForward = useCallback(() => {
    if (solutionIndex < solutionMoves.length) {
      playSolutionMove(solutionIndex);
      setSolutionIndex(prev => prev + 1);
    }
  }, [solutionIndex, solutionMoves.length, playSolutionMove]);

  const stepBackward = useCallback(() => {
    if (solutionIndex > 0) {
      const newGame = new Chess(config.fen);
      const newIndex = solutionIndex - 1;

      for (let i = 0; i < newIndex; i++) {
        const { from, to, promotion } = parseUCI(solutionMoves[i]);
        newGame.move({ from, to, promotion });
      }

      setSolutionGame(newGame);
      setSolutionIndex(newIndex);

      if (newIndex > 0) {
        const prevUci = solutionMoves[newIndex - 1];
        const { from, to } = parseUCI(prevUci);
        const isPlayerMove = (newIndex - 1) % 2 === 0;
        const color = isPlayerMove ? 'rgba(0, 200, 0, 0.5)' : 'rgba(255, 165, 0, 0.5)';
        setHighlightSquares({
          [from]: { background: color },
          [to]: { background: color },
        });
      } else {
        setHighlightSquares({});
      }
    }
  }, [solutionIndex, solutionMoves, config.fen]);

  const resetSolution = useCallback(() => {
    const newGame = new Chess(config.fen);
    setSolutionGame(newGame);
    setSolutionIndex(0);
    setHighlightSquares({});
    setIsPlayingSolution(false);
  }, [config.fen]);

  const playAll = useCallback(() => {
    resetSolution();
    setTimeout(() => setIsPlayingSolution(true), 100);
  }, [resetSolution]);

  // Handle piece drop (for interactive mode)
  const onPieceDrop = useCallback((sourceSquare: Square, targetSquare: Square, piece: Piece): boolean => {
    if (disabled || showSolution || waitingForOpponent || puzzleComplete || wrongMove) return false;

    const game = gameRef.current;

    try {
      const isPawn = piece[1] === 'P';
      const isPromotion = isPawn && (targetSquare[1] === '8' || targetSquare[1] === '1');

      const move = game.move({
        from: sourceSquare,
        to: targetSquare,
        promotion: isPromotion ? 'q' : undefined,
      });

      if (move === null) return false;

      const playerUci = toUCI(sourceSquare, targetSquare, move.promotion);
      setGameKey(k => k + 1);

      // Record the move
      const newMoveHistory = [...moveHistory, playerUci];
      setMoveHistory(newMoveHistory);

      // Check if move matches expected solution
      const expectedMove = solutionMoves[puzzleMoveIndex];
      const isCorrectMove = playerUci === expectedMove;

      if (!isCorrectMove) {
        // Wrong move - let it stay, mark as wrong, submit
        setWrongMove(true);
        setHighlightSquares({
          [sourceSquare]: { background: 'rgba(255, 0, 0, 0.5)' },
          [targetSquare]: { background: 'rgba(255, 0, 0, 0.5)' },
        });

        // Submit the wrong answer after a moment
        setTimeout(() => {
          if (!hasSubmitted.current) {
            hasSubmitted.current = true;
            onSubmit(newMoveHistory.join(' '));
          }
        }, 1000);

        return true;
      }

      // Correct move!
      setHighlightSquares({
        [sourceSquare]: { background: 'rgba(0, 200, 0, 0.5)' },
        [targetSquare]: { background: 'rgba(0, 200, 0, 0.5)' },
      });

      const nextMoveIndex = puzzleMoveIndex + 1;
      setPuzzleMoveIndex(nextMoveIndex);

      // Check if there's an opponent response to play
      if (nextMoveIndex < solutionMoves.length) {
        setWaitingForOpponent(true);

        // Play opponent's move after delay
        setTimeout(() => {
          const opponentUci = solutionMoves[nextMoveIndex];
          const { from, to, promotion } = parseUCI(opponentUci);

          try {
            gameRef.current.move({ from, to, promotion });
            setGameKey(k => k + 1);
            setPuzzleMoveIndex(nextMoveIndex + 1);

            // Highlight opponent's move in orange
            setHighlightSquares({
              [from]: { background: 'rgba(255, 165, 0, 0.5)' },
              [to]: { background: 'rgba(255, 165, 0, 0.5)' },
            });

            // Check if puzzle is complete
            if (nextMoveIndex + 1 >= solutionMoves.length) {
              setPuzzleComplete(true);
            }
          } catch (e) {
            console.error('Failed to play opponent move:', opponentUci, e);
          }

          setWaitingForOpponent(false);
        }, 600);
      } else {
        // No more moves - puzzle complete
        setPuzzleComplete(true);
      }

      return true;
    } catch {
      return false;
    }
  }, [disabled, showSolution, waitingForOpponent, puzzleComplete, wrongMove, puzzleMoveIndex, solutionMoves, moveHistory, onSubmit]);

  // Undo last move
  const undoMove = useCallback(() => {
    if (moveHistory.length === 0 || waitingForOpponent) return;

    // Rebuild game state from scratch
    const newGame = new Chess(config.fen);
    const newMoveHistory = moveHistory.slice(0, -1);

    // Replay moves
    let newPuzzleIndex = 0;
    for (let i = 0; i < newMoveHistory.length; i++) {
      // Play player move
      const playerUci = newMoveHistory[i];
      const { from: pFrom, to: pTo, promotion: pPromo } = parseUCI(playerUci);
      newGame.move({ from: pFrom, to: pTo, promotion: pPromo });
      newPuzzleIndex++;

      // Play opponent response if exists
      if (newPuzzleIndex < solutionMoves.length) {
        const oppUci = solutionMoves[newPuzzleIndex];
        const { from: oFrom, to: oTo, promotion: oPromo } = parseUCI(oppUci);
        newGame.move({ from: oFrom, to: oTo, promotion: oPromo });
        newPuzzleIndex++;
      }
    }

    gameRef.current = newGame;
    setGameKey(k => k + 1);
    setMoveHistory(newMoveHistory);
    setPuzzleMoveIndex(newPuzzleIndex);
    setPuzzleComplete(false);
    setWrongMove(false);
    hasSubmitted.current = false;
    setHighlightSquares({});
  }, [config.fen, moveHistory, solutionMoves, waitingForOpponent]);

  // Reset to starting position
  const resetPuzzle = useCallback(() => {
    gameRef.current = new Chess(config.fen);
    setGameKey(k => k + 1);
    setMoveHistory([]);
    setPuzzleMoveIndex(0);
    setPuzzleComplete(false);
    setWrongMove(false);
    hasSubmitted.current = false;
    setHighlightSquares({});
  }, [config.fen]);

  // Determine which position to show
  const displayPosition = showSolution && solutionGame
    ? solutionGame.fen()
    : gameRef.current.fen();

  // Calculate expected number of player moves
  const expectedPlayerMoves = Math.ceil(solutionMoves.length / 2);

  return (
    <div className="chess-puzzle" key={gameKey}>
      <div className="chess-board-container" style={{ maxWidth: '400px', margin: '0 auto' }}>
        <Chessboard
          position={displayPosition}
          onPieceDrop={onPieceDrop}
          boardOrientation={boardOrientation}
          customSquareStyles={highlightSquares}
          arePiecesDraggable={!disabled && !showSolution && !waitingForOpponent && !puzzleComplete}
          boardWidth={400}
          animationDuration={200}
        />
      </div>

      {/* Solution playback controls */}
      {showSolution && solutionMoves.length > 0 && (
        <div style={{ marginTop: '12px' }}>
          <div style={{
            padding: '8px 12px',
            background: '#e8f5e9',
            borderRadius: '4px',
            marginBottom: '8px',
            fontSize: '14px',
          }}>
            <strong>Solution:</strong>{' '}
            {solutionMoves.map((move, i) => (
              <span
                key={i}
                style={{
                  fontFamily: 'monospace',
                  padding: '2px 4px',
                  margin: '0 2px',
                  borderRadius: '3px',
                  background: i < solutionIndex
                    ? (i % 2 === 0 ? 'rgba(0, 200, 0, 0.3)' : 'rgba(255, 165, 0, 0.3)')
                    : '#ddd',
                  fontWeight: i === solutionIndex - 1 ? 'bold' : 'normal',
                }}
              >
                {move}
              </span>
            ))}
          </div>

          <div style={{
            display: 'flex',
            gap: '8px',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}>
            <button
              onClick={resetSolution}
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                background: '#fff',
                cursor: 'pointer',
              }}
            >
              Reset
            </button>
            <button
              onClick={stepBackward}
              disabled={solutionIndex === 0}
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                background: solutionIndex === 0 ? '#eee' : '#fff',
                cursor: solutionIndex === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              Back
            </button>
            <button
              onClick={stepForward}
              disabled={solutionIndex >= solutionMoves.length}
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                background: solutionIndex >= solutionMoves.length ? '#eee' : '#fff',
                cursor: solutionIndex >= solutionMoves.length ? 'not-allowed' : 'pointer',
              }}
            >
              Next
            </button>
            <button
              onClick={playAll}
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                border: 'none',
                background: '#4CAF50',
                color: 'white',
                cursor: 'pointer',
              }}
            >
              Play All
            </button>
          </div>

          <div style={{
            marginTop: '8px',
            fontSize: '12px',
            color: '#666',
            textAlign: 'center',
          }}>
            Move {solutionIndex} of {solutionMoves.length}
            {' • '}
            <span style={{ color: 'rgba(0, 150, 0, 1)' }}>■</span> Your moves
            {' '}
            <span style={{ color: 'rgba(200, 130, 0, 1)' }}>■</span> Opponent moves
          </div>
        </div>
      )}

      {/* Interactive mode controls */}
      {!showSolution && (
        <>
          {/* Status display */}
          <div className="puzzle-status" style={{
            marginTop: '12px',
            padding: '8px 12px',
            background: wrongMove ? '#ffebee' : waitingForOpponent ? '#e8f5e9' : puzzleComplete ? '#e8f5e9' : '#f5f5f5',
            borderRadius: '4px',
            minHeight: '32px',
            fontSize: '14px',
            textAlign: 'center',
          }}>
            {wrongMove ? (
              <span style={{ color: '#c62828' }}>Wrong! Submitting...</span>
            ) : waitingForOpponent ? (
              <span style={{ color: '#2e7d32' }}>Correct! Opponent responding...</span>
            ) : puzzleComplete ? (
              <span style={{ color: '#2e7d32' }}>Puzzle complete! Submitting...</span>
            ) : (
              <span>
                Find the best move ({moveHistory.length + 1} of {expectedPlayerMoves})
              </span>
            )}
          </div>

          {/* Move history */}
          {moveHistory.length > 0 && (
            <div className="move-history" style={{
              marginTop: '8px',
              padding: '8px 12px',
              background: '#f5f5f5',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '14px',
            }}>
              Your moves: {moveHistory.join(' ')}
            </div>
          )}

          {/* Puzzle info */}
          {config.rating && (
            <div className="puzzle-info" style={{
              marginTop: '8px',
              fontSize: '12px',
              color: '#666',
            }}>
              Rating: {config.rating}
              {config.themes && config.themes.length > 0 && (
                <span> | {config.themes.join(', ')}</span>
              )}
            </div>
          )}

          {/* Controls - hidden once puzzle ends (complete or wrong) */}
          {!disabled && !puzzleComplete && !wrongMove && (
            <div className="puzzle-controls" style={{
              marginTop: '16px',
              display: 'flex',
              gap: '8px',
              justifyContent: 'center',
            }}>
              <button
                onClick={undoMove}
                disabled={moveHistory.length === 0 || waitingForOpponent}
                style={{
                  padding: '8px 16px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  background: moveHistory.length === 0 || waitingForOpponent ? '#eee' : '#fff',
                  cursor: moveHistory.length === 0 || waitingForOpponent ? 'not-allowed' : 'pointer',
                }}
              >
                Undo
              </button>
              <button
                onClick={resetPuzzle}
                disabled={moveHistory.length === 0 || waitingForOpponent}
                style={{
                  padding: '8px 16px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  background: moveHistory.length === 0 || waitingForOpponent ? '#eee' : '#fff',
                  cursor: moveHistory.length === 0 || waitingForOpponent ? 'not-allowed' : 'pointer',
                }}
              >
                Reset
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
